import { useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import PageLayout from "./components/PageLayout";
import { useShop } from "./context/ShopContext";
import PendingReceivePopup from "./components/PendingReceive";
import PendingTransferPopup from "./components/PendingTransfer";
import "./App.css";
import "./responsive.css";

/* Pages that manage their own full-page layout */
const STANDALONE_ROUTES = ["/home", "/pos"];

/* Semantic back-navigation map — each route maps to its logical parent section */
const BACK_TARGET = {
  /* पर्चा — Sales */
  "/desistockentry":        "/home",
  "/stock":                 "/home",
  "/desi-stock-reverse":    "/home",
  "/stock-reverse":         "/home",
  "/stockmanagement":       "/home",
  "/desistock":             "/home",
  /* आमद भेजान — Stock Movement */
  "/editdesistockwithdate": "/home",
  "/editstockwithdate":     "/home",
  "/editdesitransferstock": "/home",
  "/edittransferstock":     "/home",
  "/boxenglish":            "/home",
  "/boxdesi":               "/home",
  "/boxenglishtransfer":    "/home",
  "/boxdesitransfer":       "/home",
  /* खर्च — Expenses & Finance */
  "/expensedate":           "/home",
  "/expense":               "/home",
  "/commission":            "/home",
  "/commissiondesi":        "/home",
  "/employee":              "/home",
  "/creditdebit":           "/home",
  "/productratedifference": "/home",
  "/phonepaycredit":        "/home",
  /* रिपोर्ट — Reports */
  "/salesreport":           "/home",
  "/expensereport":         "/home",
  "/report":                "/home",
  "/printslip":             "/home",
};

function PageWrapper() {
  const { pathname } = useLocation();
  if (STANDALONE_ROUTES.includes(pathname)) return <Outlet />;
  return (
    <PageLayout>
      <Outlet />
    </PageLayout>
  );
}

const MINI_SIDEBAR_ROUTES = ["/stock", "/desistockentry"];

function App() {
  const { shopId } = useShop();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen,  setSidebarOpen]  = useState(false);
  const [sidebarMini,  setSidebarMini]  = useState(false);
  const [isMobile,     setIsMobile]     = useState(window.innerWidth < 1364);

  useEffect(() => {
    const handle = () => setIsMobile(window.innerWidth < 1364);
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, []);

  useEffect(() => {
    if (!isMobile) setSidebarMini(MINI_SIDEBAR_ROUTES.includes(pathname));
  }, [pathname, isMobile]);

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);
  const toggleMini    = () => setSidebarMini((prev) => !prev);
  const sidebarW      = isMobile ? 0 : sidebarMini ? 68 : 240;

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: "#F5F6FA" }}>

      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} mini={sidebarMini} toggleMini={toggleMini} />

      {/* Main content area — offset by sidebar width on desktop */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0, marginLeft: sidebarW, transition: "margin-left 0.25s ease" }}>

        {/* Top bar — always visible on both mobile and desktop */}
        <div style={{
          height: 48, background: "#3D0D26", display: "flex",
          alignItems: "center", padding: "0 14px",
          justifyContent: "space-between", flexShrink: 0,
          borderBottom: "1px solid rgba(255,255,255,0.10)",
          position: "relative",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button
              onClick={isMobile ? toggleSidebar : toggleMini}
              title={isMobile ? "Open menu" : sidebarMini ? "Expand sidebar" : "Collapse sidebar"}
              style={{
                background: "rgba(255,255,255,0.08)", border: "none",
                borderRadius: 8, color: "#fff", fontSize: 18,
                width: 36, height: 36, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "background 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.16)"}
              onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.08)"}
            >☰</button>
            {!STANDALONE_ROUTES.includes(pathname) && (
              <button
                onClick={() => navigate(-1)}
                title="Go back"
                style={{
                  background: "rgba(255,255,255,0.08)", border: "none",
                  borderRadius: 8, color: "#fff", fontSize: 13,
                  height: 36, paddingLeft: 10, paddingRight: 12, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 5, fontWeight: 600,
                  transition: "background 0.15s",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.16)"}
                onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.08)"}
              >
                <span style={{ fontSize: 16, lineHeight: 1 }}>←</span>
              </button>
            )}
          </div>
          {isMobile && <span style={{ position: "absolute", left: "50%", transform: "translateX(-50%)", color: "#fff", fontWeight: 700, fontSize: 15, pointerEvents: "none" }}>🍷 WINESHOP</span>}
          <div style={{ width: 36 }} />
        </div>

        {/* Scrollable page content */}
        <div style={{ flex: 1, overflowY: "auto", overflowX: "clip", WebkitOverflowScrolling: "touch" }}>
          <PageWrapper />
        </div>

      </div>

      {/* Global popups */}
      {shopId && <PendingReceivePopup shopId={shopId} />}
      {shopId && <PendingTransferPopup shopId={shopId} />}
    </div>
  );
}

export default App;
